import {
  BrandSpecificWithFlag,
  BRAND_SPECIFIC_FLAG_KEY,
  BRAND_SPECIFIC_FLAG_VALUE,
  Translated,
} from '@hyphengroup/cms-components/utils/translations/staticText';
import { PageFragment } from '__generated__/graphql';
import { useLocale } from 'hooks/useLocale';
import {
  Brand,
  localeSpecificGroup,
  LocaleSpecificGroup,
} from 'utils/override';

export * from '@hyphengroup/cms-components/utils/translations/staticText';

/**
 * Retrieve keys from the refapp data dictionary.
 *
 * @param keys CMS keys to retrieve. This needs to be marked with `as const` to
 *   allow working type checking.
 */
export function dataDictionary<T extends readonly string[]>(
  keys: T,
): LocaleSpecificGroup<{ [key in T[number]]: string }> {
  return Object.fromEntries(
    keys.map((key) => [key, { _: key }]),
  ) as LocaleSpecificGroup<{ [key in T[number]]: string }>;
}

type Dictionary = Record<string, unknown>;

/** Removes unnecessary keys from a translation object */
export function filterKeys<X extends LocaleSpecificGroup<Dictionary>>(
  keys: X | BrandSpecificWithFlag<X>,
  obj: Translated<X>,
): Translated<X> {
  const _keys =
    BRAND_SPECIFIC_FLAG_KEY in keys &&
    keys[BRAND_SPECIFIC_FLAG_KEY] === BRAND_SPECIFIC_FLAG_VALUE
      ? keys._
      : keys;

  const f = {} as Record<string, unknown>;
  for (const key of Object.keys(_keys)) {
    f[key] = obj[key];
  }
  return f as Translated<X>;
}

/** Return the translation keys for the current locale */
export function useStaticText<T extends LocaleSpecificGroup<Dictionary>>(
  data: PageFragment | undefined,
  text: T,
): Translated<T> {
  return extractStaticText(useLocale().locale, data, text);
}

/** Return the translation keys for a given locale */
export function extractStaticText<T extends LocaleSpecificGroup<Dictionary>>(
  locale: string,
  data: PageFragment | undefined,
  group: T,
): Translated<T> {
  const result = {} as Record<string, unknown>;
  const text = data?.pageConfig?.staticPageText?.text;
  if (text) {
    for (const entry of text) {
      if (entry == null) continue;
      const { key, display } = entry;
      if (key && key !== display) {
        result[key] = display;
      }
    }
  }
  // Always override server data with local translation to avoid unexpected keys
  const groups = localeSpecificGroup(group).get(locale);
  for (const [key, value] of Object.entries(groups)) {
    if (key !== value) {
      result[key] = value;
    }
  }
  result.toJSON = () => {
    // Throw an error when all keys are being sent to the client
    // See the readme for more info
    throw new Error(
      'Static keys should not be serialized. Missed a filterKeys call?',
    );
  };
  return result as Translated<T>;
}

export function mockTranslation<T extends LocaleSpecificGroup<unknown>>(
  t: T,
): Translated<T> {
  const result = localeSpecificGroup(t).get('en-sg');
  return result as Translated<T>;
}

export type TranslationWrapper<T> = Partial<
  Brand<Partial<LocaleSpecificGroup<T>>>
>;
export type TranslationUnWrapper<T> =
  T extends TranslationWrapper<infer U> ? U : never;
